<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Text, Stack } from '@Shelly/shelly-design-system';
	import { devices } from '../../../stores';
	import { flowEditorState } from '../../../stores/flow_editor';
	import type { FlowNode } from '../types/flow-types';
	import type { ISceneObj } from '../../../types/SceneType';
	import { getEffectiveNodeDisplayLabel } from './helpers/LabelGenerator';
	import TriggerInspector from './Triggers/TriggerInspector.svelte';
	import MathConditionInspector from './Conditions/MathConditionInspector.svelte';
	import SceneMetadataInspector from './SceneMetadataInspector.svelte';
	import LogicGateInspector from './LogicGateInspector.svelte';
	import SwitchInspector from './SwitchInspector.svelte';
	import NoteInspector from './NoteInspector.svelte';
	import ActionSequenceInspector from './ActionSequenceInspector.svelte';
	import TemplateCardInspector from './TemplateCardInspector.svelte';
	import { getFlowCardDefinition } from '../core/card-engine/registry';
	import { validateFlowWorkspace } from '../helpers/nodeValidation';
	import { resolveFlowNodeTypeLabel } from '../helpers/flowNodeCardPresentation';
	import { getFlowCardVisualProfile } from '../helpers/flowCardVisualLanguage';

	export let scene: ISceneObj | null = null;
	export let showScene: boolean = true;
	export let showNode: boolean = true;
	export let showValidation: boolean = true;
	export let showHistory: boolean = true;
	export let compactNodeEditor: boolean = false;
	export let onFocusNode: ((nodeId: string) => void) | undefined = undefined;

	let selectedNode: FlowNode | undefined;

	$: {
		if ($flowEditorState.selectedNodeId) {
			selectedNode = $flowEditorState.nodes.find((n) => n.id === $flowEditorState.selectedNodeId);
		} else {
			selectedNode = undefined;
		}
	}

	$: panelHeaderLabel = selectedNode
		? (() => {
				if (selectedNode.type === 'note') return 'Note';
				$devices;
				return getEffectiveNodeDisplayLabel(selectedNode);
			})()
		: '';
	$: panelTypeLabel = selectedNode
		? resolveFlowNodeTypeLabel(selectedNode.type, selectedNode.data)
		: '';
	$: visualProfile = selectedNode
		? getFlowCardVisualProfile(selectedNode.type, selectedNode.data)
		: undefined;

	$: validationIssues = (() => {
		return validateFlowWorkspace($flowEditorState.nodes, $flowEditorState.edges).issues;
	})();

	$: historyEntries = [...(scene?._meta?.flowExecutionHistory ?? [])].slice(-5).reverse();

	function formatHistoryTs(ts: number): string {
		try {
			return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
		} catch {
			return '';
		}
	}

	function historyLabel(status: string | undefined): string {
		if (status === 'matched') return $format('flow_history_passed', { default: 'Passed' });
		if (status === 'not_matched')
			return $format('flow_history_not_matched', { default: 'Not matched' });
		if (status === 'error') return $format('flow_history_error', { default: 'Error' });
		return status || $format('flow_history_run', { default: 'Run' });
	}

	function handleUpdate(event: CustomEvent<FlowNode>) {
		const updatedNode = event.detail;
		if (updatedNode && updatedNode.id) {
			flowEditorState.updateNode(updatedNode.id, updatedNode);
		}
	}
</script>

<div class="property-panel-scroll">
	<Stack gap="m">
		{#if showScene}
			<section class="inspector-block scene-block">
				<SceneMetadataInspector embedded />
			</section>
		{/if}

		{#if showNode}
			<section class="inspector-block">
				{#if !compactNodeEditor}
					<Text variant="title" size="s" class="section-heading">
						{$format('flow_selected_node', { default: 'Selected node' })}
					</Text>
				{/if}

				{#if selectedNode}
					{#if !compactNodeEditor}<div class="node-summary">
							<span
								class="node-summary-icon"
								style="background: {selectedNode.data.color || '#64748b'};"
								aria-hidden="true"
							>
								<i class="fa {selectedNode.data.icon || 'fa-circle'}" />
							</span>
							<div class="node-summary-text">
								<Text variant="body" size="m" class="node-summary-title"
									>{panelHeaderLabel}</Text
								>
								<Text variant="secondary" size="sm" class="node-summary-type">
									{panelTypeLabel}
								</Text>
							</div>
						</div>{/if}

					{#if visualProfile && !compactNodeEditor}
						<div
							class="card-language"
							class:card-language-special={visualProfile.kindLabel !== 'Standard card'}
						>
							<div class="card-language-tags">
								<span class="role-chip role-{visualProfile.role}">
									<i class="fas {visualProfile.roleIcon}" aria-hidden="true"></i>
									{visualProfile.roleLabel}
								</span>
								<span class="kind-chip">
									{#if visualProfile.isComposite}<i
											class="fas fa-layer-group"
											aria-hidden="true"
										></i>{/if}
									{visualProfile.kindLabel}
								</span>
							</div>
							{#if visualProfile.dependencies.length}
								<div class="dependency-list" aria-label="Requirements">
									{#each visualProfile.dependencies as dependency}
										<span class="dependency-chip dependency-{dependency.tone}">
											<i class="fas {dependency.icon}" aria-hidden="true"></i>
											{dependency.label}
										</span>
									{/each}
								</div>
							{/if}
							<details class="how-it-works">
								<summary
									><i class="fas fa-circle-info" aria-hidden="true"></i> How it works</summary
								>
								<p>{visualProfile.howItWorks}</p>
								<dl>
									<div>
										<dt>Uses</dt>
										<dd>{visualProfile.inputMeaning}</dd>
									</div>
									<div>
										<dt>Continues</dt>
										<dd>{visualProfile.outputMeaning}</dd>
									</div>
								</dl>
							</details>
						</div>
					{/if}

					{#if selectedNode.errors && selectedNode.errors.length > 0}
						<div class="errors">
							{#each selectedNode.errors as error}
								<Text variant="body" size="s" class="error-text">⚠ {error}</Text>
							{/each}
						</div>
					{/if}

					<div class="inspector-forms">
						{#if selectedNode.type === 'trigger'}
							<TriggerInspector node={selectedNode} on:update={handleUpdate} />
						{:else if selectedNode.type === 'mathCondition'}
							<MathConditionInspector nodeId={selectedNode.id} />
						{:else if selectedNode.type === 'logicGate'}
							<LogicGateInspector nodeId={selectedNode.id} />
						{:else if selectedNode.type === 'switchNode'}
							<SwitchInspector nodeId={selectedNode.id} />
						{:else if selectedNode.type === 'variableSwitch'}
							<SwitchInspector nodeId={selectedNode.id} variant="variable" />
						{:else if selectedNode.type === 'hysteresisCondition'}
							<SwitchInspector nodeId={selectedNode.id} variant="hysteresis" />
						{:else if selectedNode.type === 'actionSequence'}
							<ActionSequenceInspector nodeId={selectedNode.id} />
						{:else if selectedNode.type === 'note'}
							<NoteInspector nodeId={selectedNode.id} />
						{:else if getFlowCardDefinition(selectedNode.type)}
							<TemplateCardInspector node={selectedNode} />
						{/if}
					</div>
				{:else}
					<Text variant="secondary" size="sm" class="select-hint">
						{$format('label_select_node_to_configure', {
							default: 'Select a node on the canvas to edit its parameters.',
						})}
					</Text>
				{/if}
			</section>
		{/if}

		{#if showValidation}
			<section class="inspector-block validation-block">
				<Text variant="title" size="s" class="section-heading">
					{$format('label_validation', { default: 'Validation' })}
				</Text>
				{#if validationIssues.length === 0}
					<div class="validation-ok">
						<Text variant="body" size="s">
							{$format('flow_validation_ready', {
								default: 'No blocking issues detected for the graph.',
							})}
						</Text>
					</div>
				{:else}
					{#each validationIssues as issue}
						<button
							type="button"
							class="validation-issue issue-{issue.severity}"
							disabled={!issue.nodeId || !onFocusNode}
							on:click={() => issue.nodeId && onFocusNode?.(issue.nodeId)}
						>
							<i
								class="fas {issue.severity === 'error'
									? 'fa-circle-xmark'
									: 'fa-triangle-exclamation'}"
								aria-hidden="true"
							/>
							<span>{issue.message}</span>
							{#if issue.nodeId}<i
									class="fas fa-crosshairs focus-icon"
									aria-hidden="true"
								/>{/if}
						</button>
					{/each}
				{/if}
			</section>
		{/if}

		{#if showHistory}
			<section class="inspector-block history-block">
				<Text variant="title" size="s" class="section-heading">
					{$format('flow_test_history', { default: 'Test history' })}
				</Text>
				{#if historyEntries.length === 0}
					<Text variant="secondary" size="sm">
						{$format('flow_no_test_runs', {
							default: 'No recent test runs recorded for this flow.',
						})}
					</Text>
				{:else}
					<ul class="history-list">
						{#each historyEntries as entry}
							<li class="history-row">
								<span class="history-dot" class:error={entry.status === 'error'} />
								<span class="history-main">
									{historyLabel(entry.status)} · {formatHistoryTs(entry.ts)}
								</span>
								{#if entry.message}
									<span class="history-msg">{entry.message}</span>
								{/if}
							</li>
						{/each}
					</ul>
				{/if}
			</section>
		{/if}
	</Stack>
</div>

<style>
	.property-panel-scroll {
		padding: 0.75rem 0.85rem 1.25rem;
		height: 100%;
		overflow-y: auto;
		overflow-x: hidden;
		width: 100%;
		min-width: 0;
		box-sizing: border-box;
	}

	.inspector-block {
		padding-bottom: 0.75rem;
		border-bottom: 1px solid rgba(255, 255, 255, 0.08);
	}

	.inspector-block:last-child {
		border-bottom: none;
	}

	.scene-block {
		border-bottom: 1px solid rgba(255, 255, 255, 0.08);
		padding-bottom: 0.5rem;
	}

	.section-heading {
		text-transform: uppercase;
		letter-spacing: 0.04em;
		font-size: 0.68rem !important;
		opacity: 0.85;
		margin-bottom: 0.5rem;
	}

	.node-summary {
		display: flex;
		align-items: flex-start;
		gap: 0.5rem;
		padding: 0.5rem 0.55rem;
		margin-bottom: 0.65rem;
		border-radius: 7px;
		border: 1px solid rgba(47, 125, 246, 0.35);
		background: rgba(47, 125, 246, 0.08);
	}

	.card-language {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		margin: -0.2rem 0 0.7rem;
		padding: 0.55rem;
		border: 1px solid rgba(148, 163, 184, 0.18);
		border-radius: 8px;
		background: rgba(148, 163, 184, 0.045);
	}

	.card-language-special {
		border-color: rgba(139, 92, 246, 0.3);
		background: rgba(139, 92, 246, 0.07);
	}

	.card-language-tags,
	.dependency-list {
		display: flex;
		flex-wrap: wrap;
		gap: 0.35rem;
	}

	.role-chip,
	.kind-chip,
	.dependency-chip {
		display: inline-flex;
		align-items: center;
		gap: 0.3rem;
		border-radius: 999px;
		padding: 0.2rem 0.48rem;
		font-size: 0.63rem;
		font-weight: 750;
		line-height: 1;
	}

	.role-chip {
		border: 1px solid rgba(96, 165, 250, 0.34);
		background: rgba(59, 130, 246, 0.12);
		color: #bfdbfe;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}

	.role-action {
		border-color: rgba(34, 197, 94, 0.36);
		background: rgba(34, 197, 94, 0.12);
		color: #bbf7d0;
	}
	.role-control {
		border-color: rgba(168, 85, 247, 0.38);
		background: rgba(168, 85, 247, 0.12);
		color: #e9d5ff;
	}
	.role-delay {
		border-color: rgba(245, 158, 11, 0.4);
		background: rgba(245, 158, 11, 0.12);
		color: #fde68a;
	}
	.role-state {
		border-color: rgba(249, 115, 22, 0.4);
		background: rgba(249, 115, 22, 0.12);
		color: #fed7aa;
	}
	.role-event {
		border-color: rgba(6, 182, 212, 0.4);
		background: rgba(6, 182, 212, 0.12);
		color: #a5f3fc;
	}
	.role-note {
		border-color: rgba(249, 115, 22, 0.38);
		background: rgba(249, 115, 22, 0.1);
		color: #fed7aa;
	}

	.kind-chip {
		color: #c4b5fd;
		background: rgba(139, 92, 246, 0.11);
	}

	.dependency-chip {
		font-weight: 550;
		color: #cbd5e1;
		background: rgba(148, 163, 184, 0.1);
	}

	.dependency-warning {
		color: #fde68a;
		background: rgba(245, 158, 11, 0.12);
	}
	.dependency-info {
		color: #bae6fd;
		background: rgba(14, 165, 233, 0.11);
	}

	.how-it-works {
		border-top: 1px solid rgba(148, 163, 184, 0.14);
		padding-top: 0.45rem;
		font-size: 0.72rem;
		color: #cbd5e1;
	}

	.how-it-works summary {
		display: flex;
		align-items: center;
		gap: 0.35rem;
		cursor: pointer;
		font-weight: 650;
		color: #dbeafe;
		list-style: none;
	}

	.how-it-works summary::-webkit-details-marker {
		display: none;
	}
	.how-it-works p {
		margin: 0.55rem 0;
		line-height: 1.45;
	}
	.how-it-works dl {
		display: grid;
		gap: 0.4rem;
		margin: 0;
	}
	.how-it-works dl > div {
		display: grid;
		grid-template-columns: 4rem minmax(0, 1fr);
		gap: 0.45rem;
	}
	.how-it-works dt {
		color: #94a3b8;
		font-weight: 700;
	}
	.how-it-works dd {
		margin: 0;
		line-height: 1.4;
	}

	.node-summary-icon {
		width: 32px;
		height: 32px;
		border-radius: 7px;
		display: grid;
		place-items: center;
		color: #fff;
		font-size: 0.85rem;
		flex-shrink: 0;
	}

	.node-summary-text {
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	:global(.node-summary-title) {
		font-weight: 600 !important;
		line-height: 1.25;
		word-break: break-word;
	}

	:global(.node-summary-type) {
		text-transform: capitalize;
		opacity: 0.9;
	}

	.select-hint {
		line-height: 1.45;
	}

	.inspector-forms {
		margin-top: 0.25rem;
	}

	.errors {
		background: rgba(239, 68, 68, 0.1);
		border: 1px solid rgba(239, 68, 68, 0.3);
		border-radius: 6px;
		padding: 0.65rem;
		margin-bottom: 0.65rem;
	}

	.error-text {
		color: #ef4444;
		display: block;
		margin-bottom: 0.25rem;
	}

	.error-text:last-child {
		margin-bottom: 0;
	}

	.validation-ok {
		padding: 0.55rem 0.65rem;
		border-radius: 7px;
		background: rgba(34, 197, 94, 0.1);
		border: 1px solid rgba(34, 197, 94, 0.25);
	}

	.validation-issue {
		width: 100%;
		display: grid;
		grid-template-columns: 18px minmax(0, 1fr) 18px;
		gap: 8px;
		align-items: start;
		margin-bottom: 7px;
		padding: 10px;
		border-radius: 8px;
		border: 1px solid rgba(245, 158, 11, 0.3);
		background: rgba(245, 158, 11, 0.09);
		color: #fcd34d;
		text-align: left;
	}
	.validation-issue:not(:disabled) {
		cursor: pointer;
	}
	.validation-issue:not(:disabled):hover {
		border-color: rgba(96, 165, 250, 0.6);
	}
	.validation-issue span {
		color: #e5e7eb;
		font-size: 12px;
		line-height: 1.4;
	}
	.issue-error {
		border-color: rgba(239, 68, 68, 0.32);
		background: rgba(239, 68, 68, 0.09);
		color: #fca5a5;
	}
	.focus-icon {
		color: #93c5fd;
		margin-top: 2px;
	}

	.validation-warn {
		padding: 0.55rem 0.65rem;
		border-radius: 7px;
		background: rgba(245, 158, 11, 0.1);
		border: 1px solid rgba(245, 158, 11, 0.28);
		margin-bottom: 0.45rem;
	}

	.validation-warn:last-child {
		margin-bottom: 0;
	}

	.history-list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 0.45rem;
	}

	.history-row {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: 0.35rem 0.5rem;
		font-size: 0.78rem;
		color: #a1a1aa;
	}

	.history-dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: #22c55e;
		flex-shrink: 0;
		margin-top: 0.2rem;
	}

	.history-dot.error {
		background: #ef4444;
	}

	.history-main {
		color: #d4d4d8;
	}

	.history-msg {
		width: 100%;
		font-size: 0.72rem;
		opacity: 0.85;
	}
</style>
